import React, {useEffect} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {CustomDelete, DateTypeSelector} from 'components';
import {AddCircleBlue, FullTable, ManageActions, Input} from 'components';
import {Svg} from 'assets';
import {FindSuccess, Images, OrgIntegrations, useModal, noteModels, noteStatusEnums} from 'utils';
import {
    CLIENT_DOCUMENT_ACTION_TYPE,
    clientDocumentBody,
    clientDocumentHead,
    clientNoteEnums,
    documentNotYet
} from './constants';
import {CreateProgressReport} from '../../clientClinical/progressReport/createProgressReport';
import {FormBuilder, formBuilderStatusEnums, formModelEnums} from 'pages';
import {clientActions, formActions, httpRequestsOnSuccessActions, noteActions} from 'store';
import {NoteInfo} from '../../../../../components/modal/notes/noteInfo';

const CREATE_ACTION_TYPE = 'CREATE_GLOBAL_NOTE';
const EDIT_ACTION_TYPE = 'EDIT_GLOBAL_NOTE';
const DELETE_ACTION_TYPE = 'DELETE_CLIENT_NOTES';

export const ClientDocumentation = ({}) => {
    const params = useParams();
    const {open, close} = useModal();
    const dispatch = useDispatch();
    const client = useSelector((state) => state.client.clientItemInfo);
    const clientsNotes = useSelector((state) => state.client.clientsNotes);
    const createSuccess = FindSuccess(CREATE_ACTION_TYPE);
    const editSuccess = FindSuccess(EDIT_ACTION_TYPE);
    const location = useLocation();
    const info = location?.state;
    const navigate = useNavigate();
    const {cSession} = OrgIntegrations();

    const renderParams = () => {
        const filteredInfo = {
            ...info,
        };
        filteredInfo.skip = info?.skip ? info?.skip : 0;
        filteredInfo.limit = 50;
        delete filteredInfo.page;
        delete filteredInfo.activeTab;
        delete filteredInfo.name;
        return filteredInfo;
    };

    const getNotes = (load) => {
        dispatch(clientActions.getClientAllNotes(renderParams(), client?.id, load));
    };

    useEffect(() => {
        getNotes();
    }, [info]);

    useEffect(() => {
        dispatch(formActions.resetForms());
    }, []);


    useEffect(() => {
        if (createSuccess || editSuccess) {
            afterSuccess();

            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_ACTION_TYPE));
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_ACTION_TYPE));
        }
    }, [createSuccess, editSuccess]);


    const afterSuccess = (type) => {
        getNotes('noLoad');
    }

    const addSectionOrNote = (info, type) => {
        const isAbc = type === formModelEnums.ABC_DATA || info?.type === clientNoteEnums.ABC_NOTE;
        const model = isAbc ? formModelEnums.ABC_DATA : formModelEnums.CLIENTNOTES;
        const createEditActionType = info?.id ? EDIT_ACTION_TYPE : CREATE_ACTION_TYPE;
        const addTitle = isAbc ? (info?.id ? 'Edit ABC Data' : 'Add ABC Data') : info?.id ? 'Edit Note' : 'Add Note';

        // The forms slice is global: without a reset, the previous modal's template list, selected
        // template body and default values leak into this one (e.g. a Client Note body showing up
        // in "Add ABC Data" with no template selected).
        dispatch(formActions.resetForms());
        dispatch(formActions.resetFormById());
        dispatch(formActions.resetFormDefault());
        dispatch(formActions.getForms({model: model, status: formBuilderStatusEnums.PUBLISHED}));

        open(
            <div>
                <p className="modal-header-title" style={{marginBottom: 24}}>
                    {addTitle}
                </p>
                <FormBuilder
                    info={info}
                    resourceId={client?.id}
                    formModel={model}
                    ACTION_TYPE={createEditActionType}
                    handleSaveNote={(data) => {
                        const noteData = {
                            ...data,
                            resource: client?.id,
                            onModel: noteModels.Client,
                            name: data?.name,
                        };

                        // Without an explicit type the API defaults to a plain client NOTE, so an
                        // "Add ABC Data" entry would be filed as an ordinary note. ABC notes also
                        // carry a Draft/Completed label: this entry point offers no draft
                        // affordance (unlike the session Documentation modal), so a save here means
                        // Completed, while an edit preserves whatever status the entry already has.
                        if (isAbc) {
                            noteData.type = clientNoteEnums.ABC_NOTE;
                            noteData.status = info?.status ?? noteStatusEnums.COMPLETED;
                        }

                        delete noteData?.id;
                        if (!!info) {
                            dispatch(noteActions.editGlobalNote(client?.id, info?.id, noteData, noteModels.Client));
                        } else {
                            dispatch(noteActions.createGlobalNote(noteData));
                        }
                    }}
                    renderCustomFields={(control, form) => (
                        <div>
                            <Input
                                disabled={!form}
                                name="name"
                                label="Note Name"
                                variant="outlined"
                                control={control}
                            />
                        </div>
                    )}
                />
            </div>,

            {styles: {bodyStyle: {padding: '24px 48px'}}}
        );
    };


    const handleClickButton = (id, item) => {
        if (item?.type === clientNoteEnums.PROGRESS_REPORT) {
            const params = {
                reportTitle: item?.title,
                startDate: item?.startDate,
                endDate: item?.endDate,
            };
            navigate(`/progress-report/${item?.templateId}/client/${client?.id}/report/${item?.id}`, {state: params});
        } else if (item?.type === clientNoteEnums.ABC_NOTE) {
            addSectionOrNote(item, formModelEnums.ABC_DATA);
        } else {
            open(
                <NoteInfo
                    notEditAble={item?.type === clientNoteEnums.APPT_SESSION_NOTE}
                    note={item}
                    handleAction={(value, action) => {
                        if (action === 'edit') {
                            addSectionOrNote(value);
                        }
                        if (action === 'deleteNote') {
                            open(
                                <CustomDelete
                                    text="Are you sure you want to delete this note? This action cannot be undone."
                                    info="Delete Note"
                                    handleDel={() => dispatch(clientActions.deleteClientAllNotes(item?.id, client?.id, renderParams()))}
                                    actionType={DELETE_ACTION_TYPE}
                                    afterSuccess={close}
                                    body={
                                        <div className="deleting-client-info-box" style={{alignItems: 'flex-start'}}>
                                            <img style={{marginTop: '3px'}} src={Images.editBlack} alt="icon"/>
                                            <p style={{fontWeight: 400}}>{item?.templateTitle}</p>
                                        </div>
                                    }
                                />
                            );
                        }
                    }}
                />,
                {
                    styles: {bodyStyle: {padding: '24px 48px'}},
                }
            );
        }

    };
    return (
        <div>
            <div className="space-between">
                <DateTypeSelector outLabel={'Created Date Range'} startName={'startDate'} endName={'endDate'}
                                  reset={true}/>
                <ManageActions
                    btnClass="reset-btn"
                    renderBtnView={(isOpen) => (
                        <div className="flex-align-center" style={{gap: 6}}>
                            <AddCircleBlue text={'Add Form'}/>
                            <Svg.ArrowRight2Svg
                                style={{
                                    transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                    filter: 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(1578%) hue-rotate(201deg) brightness(90%) contrast(100%)',
                                }}
                            />
                        </div>
                    )}
                    renderBody={(closePopover) => (
                        <div className="flex" style={{flexDirection: 'column'}}>
                            {/*PermissionList.CLIENT_NOTE_MANAGE?.code*/}
                            <button
                                className="header-add-goals-items-btn"
                                onClick={() => {
                                    addSectionOrNote();
                                    closePopover?.();
                                }}>
                                Add Client Note
                            </button>
                            {cSession &&
                                <button
                                    className="header-add-goals-items-btn"
                                    onClick={() => {
                                        addSectionOrNote(null, formModelEnums.ABC_DATA);
                                        closePopover?.();
                                    }}>
                                    Add ABC Data
                                </button>
                            }
                            {cSession &&
                                <button
                                    className="header-add-goals-items-btn"
                                    onClick={() => {
                                        open(<CreateProgressReport clientId={params?.id}/>);
                                        closePopover?.();
                                    }}>
                                    Add Progress Report
                                </button>
                            }
                        </div>
                    )}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    styleBodyItems={{padding: 0}}
                />
            </div>

            <FullTable
                loadingType={CLIENT_DOCUMENT_ACTION_TYPE}
                head={clientDocumentHead}
                body={clientDocumentBody(afterSuccess, renderParams())}
                handleClick={(id, item) => handleClickButton(id, item)}
                list={clientsNotes?.data}
                listCount={clientsNotes?.count}
                notYet={documentNotYet}
                activeRowId={''}
            />
        </div>
    );
}